import { useState } from "react"
import { Link } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { DeleteIcon } from "components/ui/icons/delete/DeleteIcon"
import { CloseIcon } from "components/ui/icons/close/CloseIcon"
import { DeleteModal } from "components/ui/modals/DeleteModal/DeleteModal"
import { ProjectNumber } from "components/ui/ProjectNumber/ProjectNumber"
import { Skeleton } from "components/ui/skeleton/Skeleton"
import { TableConfiguration } from "components/ui/tables/TableConfiguration/TableConfiguration"
import { H2 } from "components/ui/typography/titles/Titles"
import {
	useCreateCustomModelNumber,
	useDeleteCustomModelNumber,
	useGetCustomModelNumbers,
	useUpdateCustomModelNumber,
} from "hooks/api/customModelNumbers/customModelNumbers.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { useMessage } from "hooks/message.hook"
import { useCurrentUserStore } from "store/user.store"
import { canActAsOwner, isAdminRole } from "helpers/drawingAccess.helper"
import type { TDrawingOwner } from "helpers/drawingAccess.helper"
import type { ColumnsType } from "antd/es/table"
import type {
	TCustomModelNumber,
	TCustomModelNumberDerivedFrom,
	TCustomModelNumberPayload,
} from "types/customModelNumbers.type"
import { CustomModelNumberModal } from "./CustomModelNumberModal"
import style from "./CustomModelNumbers.module.scss"

const {
	customModelNumbers: customModelNumbersStyles,
	header,
	titleRow,
	title,
	closeButton,
	closeIcon,
	tableWrapper,
	table,
	actions,
	actionButton,
	editButton,
	deleteButton,
	baseModelNumberLink,
	createButtonWrapper,
	createButton,
	empty,
} = style

type Props = {
	drawingId: number
	drawingName: string
	fileOwner: TDrawingOwner
	onCloseDrawer: () => void
}

const formatPrice = (price: string) => `$${Number(price).toLocaleString("en-US", { maximumFractionDigits: 2 })}`

export const CustomModelNumbers = ({ drawingId, drawingName, fileOwner, onCloseDrawer }: Props) => {
	const { currentUser } = useCurrentUserStore()
	const isAdmin = isAdminRole(currentUser.role)
	// The backend rejects create/update/delete from anyone else, so the actions are hidden rather
	// than left to fail — the same gate as `ProjectNumber` below (owner or admin, REQ-392).
	const canManage = canActAsOwner(currentUser.role, fileOwner)
	const queryClient = useQueryClient()
	const { formatError } = useApiErrorFormatter()
	const { success: successMessage } = useMessage()
	const { data: customModelNumbers, isPending: isListPending } = useGetCustomModelNumbers(drawingId)
	const { mutate: createCustomModelNumber, isPending: isCreatePending } = useCreateCustomModelNumber()
	const { mutate: updateCustomModelNumber, isPending: isUpdatePending } = useUpdateCustomModelNumber()
	const { mutate: deleteCustomModelNumber, isPending: isDeletePending } = useDeleteCustomModelNumber()
	const [editedCustomModelNumber, setEditedCustomModelNumber] = useState<TCustomModelNumber | null | undefined>(undefined)
	const [customModelNumberToDelete, setCustomModelNumberToDelete] = useState<TCustomModelNumber | null>(null)

	const isModalOpen = editedCustomModelNumber !== undefined
	const isModalPending = isCreatePending || isUpdatePending
	const invalidateCustomModelNumbers = () =>
		queryClient.invalidateQueries({ queryKey: ["customModelNumbers", drawingId] })

	const saveCustomModelNumber = (payload: TCustomModelNumberPayload) => {
		if (editedCustomModelNumber === null) {
			createCustomModelNumber(
				{ drawingId, payload },
				{
					onSuccess: () => {
						invalidateCustomModelNumbers()
						setEditedCustomModelNumber(undefined)
						successMessage("Custom model number created successfully")
					},
					onError: formatError,
				},
			)
			return
		}

		if (editedCustomModelNumber === undefined) return

		updateCustomModelNumber(
			{ drawingId, customModelNumberId: editedCustomModelNumber.id, payload },
			{
				onSuccess: () => {
					invalidateCustomModelNumbers()
					setEditedCustomModelNumber(undefined)
					successMessage("Custom model number updated successfully")
				},
				onError: formatError,
			},
		)
	}

	const deleteSelectedCustomModelNumber = () => {
		if (customModelNumberToDelete === null) return

		deleteCustomModelNumber(
			{ drawingId, customModelNumberId: customModelNumberToDelete.id },
			{
				onSuccess: () => {
					invalidateCustomModelNumbers()
					setCustomModelNumberToDelete(null)
					successMessage("Custom model number deleted successfully")
				},
				onError: formatError,
			},
		)
	}

	// Widths start from the design's measured column proportions (126/214/88/88/88/88/96/155 px
	// on its 1440px frame) — the previous, much wider guesses made the table overflow the drawer
	// and run under the fixed-width left menu instead of fitting the available content area.
	const columns: ColumnsType<TCustomModelNumber> = [
		// 126px (the design's own measurement) clips the "MODEL NUMBER" header in the app's real
		// font — measured at ~99px for the label alone, +32px of antd's default cell padding is
		// already 131px. 140px keeps the header on one line with a little room to spare.
		{ title: "MODEL NUMBER", dataIndex: "model_number", key: "model_number", width: 140 },
		// No fixed width: NAME is the one column that fills whatever space the others don't
		// need, so the table (and its header's bottom border) always stretches to the full
		// width of its container instead of stopping short and leaving a bare gap — per design.
		{ title: "NAME", dataIndex: "name", key: "name" },
		{ title: "PRICE", dataIndex: "price", key: "price", width: 88, render: formatPrice },
		{ title: "WIDTH", dataIndex: "width", key: "width", width: 88 },
		{ title: "HEIGHT", dataIndex: "height", key: "height", width: 88 },
		{ title: "DEPTH", dataIndex: "depth", key: "depth", width: 88 },
		{
			title: "BASE MODEL NUMBER",
			dataIndex: "derived_from",
			key: "derived_from",
			// Longer header label than MODEL NUMBER's — needs more room to avoid antd's
			// header-cell ellipsis truncating the title itself.
			width: 200,
			// Only admins can open the model numbers configuration page, so everyone else gets plain text.
			render: (derivedFrom: TCustomModelNumberDerivedFrom | null) => {
				if (!derivedFrom) return null
				if (!isAdmin) return derivedFrom.model_number
				return (
					<Link
						className={baseModelNumberLink}
						to={`/configuration/model-numbers?modelNumberID=${derivedFrom.id}`}
						target="_blank"
						rel="noopener noreferrer"
					>
						{derivedFrom.model_number}
					</Link>
				)
			},
		},
		{
			title: "USED IN",
			dataIndex: "instances_count",
			key: "instances_count",
			width: 96,
			render: (instancesCount: number) => (instancesCount > 0 ? `${instancesCount} objects` : ""),
		},
		...(canManage
			? [
					{
						title: "",
						key: "actions",
						width: 155,
						render: (_: unknown, record: TCustomModelNumber) => (
							<div className={actions}>
								<button className={`${actionButton} ${editButton}`} type="button" onClick={() => setEditedCustomModelNumber(record)}>
									Edit
								</button>
								<button
									className={`${actionButton} ${deleteButton}`}
									type="button"
									onClick={() => setCustomModelNumberToDelete(record)}
									aria-label={`Delete ${record.model_number}`}
								>
									<DeleteIcon />
								</button>
							</div>
						),
					},
				]
			: []),
	]

	return (
		<div className={customModelNumbersStyles}>
			<div className={header}>
				<div className={titleRow}>
					<H2 className={title}>{drawingName}</H2>
					<ProjectNumber editable={canManage} />
				</div>
				<button className={closeButton} type="button" onClick={onCloseDrawer} aria-label="Close custom model numbers drawer">
					<CloseIcon className={closeIcon} />
				</button>
			</div>
			<div className={tableWrapper}>
				{isListPending ? (
					<Skeleton rows={8} />
				) : customModelNumbers?.length ? (
					<TableConfiguration
						className={table}
						columns={columns}
						data={customModelNumbers.map((item) => ({ ...item, key: item.id }))}
						tableLayout="fixed"
					/>
				) : (
					<div className={empty}>No custom model numbers have been created for this drawing yet.</div>
				)}
			</div>
			{canManage ? (
				<div className={createButtonWrapper}>
					<ButtonPrimary className={createButton} onClick={() => setEditedCustomModelNumber(null)}>
						Create model number
					</ButtonPrimary>
				</div>
			) : null}
			{canManage ? (
				<>
					<CustomModelNumberModal
						customModelNumber={editedCustomModelNumber ?? null}
						isOpen={isModalOpen}
						isPending={isModalPending}
						onClose={() => setEditedCustomModelNumber(undefined)}
						onSave={saveCustomModelNumber}
					/>
					<DeleteModal
						isModalOpen={customModelNumberToDelete !== null}
						isPending={isDeletePending}
						setModalClose={() => setCustomModelNumberToDelete(null)}
						deleteHandle={deleteSelectedCustomModelNumber}
						text={`Delete ${customModelNumberToDelete?.model_number ?? "custom model number"}?`}
						title={
							customModelNumberToDelete?.instances_count
								? `${customModelNumberToDelete.instances_count} objects will remain, but their custom model number link will be cleared.`
								: "No objects use this custom model number."
						}
					/>
				</>
			) : null}
		</div>
	)
}
